import { UserAvatar } from '@/components'
import { useAuth, useBrowserQuery, useLocalStorage } from '@/hooks/common'
import { useGetBasicInfo } from '@/hooks/api/dashboard'
import { Box, Button, CircularProgress, Divider, IconButton, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { UserDetailsContent } from '../__styled'
import dayjs from 'dayjs'
import UserConnections from './Connections'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-hot-toast'
import { ScrollableCapsulesTabBar } from '@/components/ScrollableCapsulesTabBar'
import { PrivacyType, PrivacyTypes, TopicType, TopicTypes } from '@/utils/constants'
import TopicItemsGrid from '@/pages/home/molecules/TopicItemsGrid'
import { ConnectionModel, ConnListAction, SearchType, Topic } from '@/types'
import { useTopicsListViewModel } from '@/hooks/api/dashboard/viewModels/useTopicsListViewModel'
import { useConnectionsListViewModel } from '@/hooks/api/dashboard/viewModels/useConnectionsListViewModel'
import { ConnectionsListContext } from '@/context/ConnectionsListContext'
import NoDataPlaceholder from '@/components/NoDataPlaceholder'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import CirclesSelectDrawer from './molecules/CirclesSelectDrawer'
import { FollowActionButton, RequestActionButton } from '@/components/StyledComponents'
import { useManageConnections } from '@/hooks/api/dashboard/useManageConnections'
import { useSubscription } from '@/context/SubscriptionContext'
import { FEATURE_KEYS } from '@/utils/subscriptions'
import { LoadingButton } from '@mui/lab'
import colors from '@/styles/colors'

const topicTypesToShow = [TopicType.PATTERN, TopicType.ALBUM]

export default function UserDetails() {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  const selectedTopicType =
    selectedTabIndex < topicTypesToShow.length ? topicTypesToShow[selectedTabIndex] : TopicType.ALL

  const { user: currentUser } = useAuth()
  const [selectedUserForDrawer, setSelectedUserForDrawer] = useState<ConnectionModel | null>(null)

  const patternTopicsViewModel = useTopicsListViewModel()
  const collectionTopicListViewModel = useTopicsListViewModel()

  const {
    getTopics: getPatternTopics,
    topicsList: patternTopics,
    topicsCount: totalPatterns,
  } = patternTopicsViewModel
  const {
    getTopics: getCollectionTopics,
    topicsList: collectionTopics,
    topicsCount: totalCollections,
  } = collectionTopicListViewModel
  const { topicsList, loading, canLoadNextPage, loadingNextPage, loadNextPageData, getTopics } =
    selectedTopicType == TopicType.ALBUM ? collectionTopicListViewModel : patternTopicsViewModel

  const connectionsViewModel = useConnectionsListViewModel()
  const {
    loading: loadingConnections,
    loadingNextPage: loadingConnNextPage,
    canLoadNextPage: canLoadConnNextPage,
    connections,
    getConnectionsList,
    loadNextPageData: loadNextPageConnData,
    pagination: connPagination,
  } = connectionsViewModel

  const navigate = useNavigate()
  const query = useBrowserQuery()
  const userId = query.get('id')
  const getUserInfo = useGetBasicInfo(userId || '')
  const {
    acceptConnectionInvite,
    rejectConnectionInvite,
    sendFollowRequest,
    blockConnection,
    removeConnection,
  } = useManageConnections()

  const { requirePro, isFeatureAvailable } = useSubscription()

  const [selectedUserProfile] = useAppStore((state) => [state.user.selectedUserProfile], shallow)

  const connectionsTabSelected = selectedTabIndex == 2

  useEffect(() => {
    if (userId) {
      getUserInfo.mutate({ isUserProfile: true })
      //selectedTabIndex(0)
    }
  }, [userId])

  useEffect(() => {
    if (getUserInfo.isSuccess) {
      loadUserTopics()
      loadUserConnections()
    }
  }, [getUserInfo.isSuccess])

  const handleTabSelection = (index: number) => {
    const onChange = () => {
      setSelectedTabIndex(index)
    }
    if (index == 1) {
      requirePro(FEATURE_KEYS.COLLECTIONS, onChange)
    } else {
      onChange()
    }
  }

  const loadUserTopics = (pageNo: number = 1) => {
    if (userId) {
      getPatternTopics({
        pageNo: pageNo,
        privacyType: PrivacyType.PUBLIC,
        topicType: TopicType.PATTERN,
        searchType: SearchType.OtherUser,
        connID: userId,
      })

      if (isFeatureAvailable(FEATURE_KEYS.COLLECTIONS)) {
        getCollectionTopics({
          pageNo: pageNo,
          privacyType: PrivacyType.PUBLIC,
          topicType: TopicType.ALBUM,
          searchType: SearchType.OtherUser,
          connID: userId,
        })
      }
    }
  }

  const loadUserConnections = (pageNo: number = 1) => {
    if (userId) {
      getConnectionsList({ connID: userId })
    }
  }

  const topics = useMemo(() => {
    if (topicsList) {
      let list = topicsList
      list = list.filter((i) => {
        const topicData = TopicTypes[Number(i.topicType)]
        const privacyData = PrivacyTypes[Number(i.visibility)]
        return (
          topicData &&
          privacyData &&
          (selectedTopicType == TopicType.ALL ? true : i.topicType == selectedTopicType.toString())
        )
      })
      // sort by date updated
      list.sort((a, b) => {
        if (a.dateUpdated > b.dateUpdated) return -1
        if (a.dateUpdated < b.dateUpdated) return 1
        return 0
      })
      return list
    }
    return []
  }, [topicsList, selectedTopicType])

  const tabTitles = useMemo(() => {
    let mapped = topicTypesToShow.map((e, i) => {
      let count = e == TopicType.PATTERN ? totalPatterns : totalCollections
      if (count && count > 0) {
        return `${TopicTypes[e].tabTitle} (${count})`
      } else {
        return `${TopicTypes[e].tabTitle} (0)`
      }
    })

    mapped.push(`Followers (${connections?.length || 0})`)

    return mapped
  }, [totalPatterns, totalCollections, connections])

  const onClickTopic = (topic: Topic) => {
    const { topicID, topicType } = topic

    let previewQuery = new URLSearchParams()
    previewQuery.set('topicID', topicID)
    previewQuery.set('topicType', topicType)
    navigate(`/preview?${previewQuery.toString()}`)
  }

  if (getUserInfo.isLoading) {
    return (
      <UserDetailsContent
        justifyContent={'center'}
        alignItems='center'
        sx={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            py: 2,
          }}
        >
          <CircularProgress
            size={48}
            thickness={4}
            sx={{
              color: '#6366f1',
            }}
          />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#64748b',
            }}
          >
            Loading profile...
          </Typography>
        </Box>
      </UserDetailsContent>
    )
  }

  if (getUserInfo.isError) {
    return (
      <UserDetailsContent
        justifyContent={'center'}
        alignItems='center'
        sx={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            padding: '40px',
            borderRadius: '16px',
            background: '#ffffff',
            boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Box
            sx={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(239, 68, 68, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '32px',
              }}
            >
              😕
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#0f172a',
            }}
          >
            User not found
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#64748b',
              textAlign: 'center',
            }}
          >
            The profile you're looking for doesn't exist or has been removed.
          </Typography>
        </Box>
      </UserDetailsContent>
    )
  }

  const profile = selectedUserProfile
  if (!profile) return null

  const isFollowing = profile.isConnection
  const showSent = !isFollowing && profile.reqReceived
  const showAccept = !isFollowing && !showSent && selectedUserProfile.reqSent //either false (BOOL) or reqID (String)

  const onClickConnect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (!sendFollowRequest.isLoading) {
      toast.promise(sendFollowRequest.mutateAsync(profile.userID), {
        loading: 'Sending Request...',
        success: <Typography variant='p'>{'Follow request has been sent.'}</Typography>,
        error: <Typography variant='p'>{'Failed to send follow request.'}</Typography>,
      })
    }
  }

  const onClickAcceptRequest = (event: React.MouseEvent<HTMLButtonElement>) => {
    // const model = convertEgoBasicInfoToConnectionModel(profile)
    // onUserAction(model, ConnListAction.ACCEPT_CONN_REQUEST)
    event.stopPropagation()
    if (!acceptConnectionInvite.isLoading) {
      toast.promise(
        acceptConnectionInvite.mutateAsync({
          connID: profile.userID,
          connReqID: `${profile.reqSentConnReqID}`,
        }),
        {
          loading: 'Accepting Request...',
          success: (
            <Typography variant='p'>{`${profile.firstName} is now a connection`}</Typography>
          ),
          error: <Typography variant='p'>{'Failed to accept follow request.'}</Typography>,
        },
      )
    }
  }

  const onClickDeclineRequest = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (!rejectConnectionInvite.isLoading) {
      toast.promise(
        rejectConnectionInvite.mutateAsync({
          connID: profile.userID,
          connReqID: `${profile.reqSentConnReqID}`,
        }),
        {
          loading: 'Processing the decline...',
          success: <Typography variant='p'>{`Success!`}</Typography>,
          error: <Typography variant='p'>{'Failed to decline the request.'}</Typography>,
        },
      )
    }
  }

  const onClickRemoveConnect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    toast.promise(removeConnection.mutateAsync(profile.userID), {
      loading: 'Removing user...',
      success: <Typography variant='p'>{`User has been removed.`}</Typography>,
      error: <Typography variant='p'>Failed to remove a user.</Typography>,
    })
  }
  const onClickBlockConnect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    toast.promise(blockConnection.mutateAsync(profile.userID), {
      loading: 'Blocking user...',
      success: <Typography variant='p'>{`User has been blocked.`}</Typography>,
      error: <Typography variant='p'>Failed to block a user.</Typography>,
    })
  }

  const renderConnectionStatus = () => {
    if (profile.userID === currentUser.userID) return null
    if (profile.userID != currentUser.userID && isFollowing) {
      return (
        <FollowActionButton aria-selected onClick={onClickRemoveConnect}>
          <span className='btn-default-text'>{'Following'}</span>
          <span className='btn-hover-text'>{'Unfollow'}</span>
        </FollowActionButton>
      )
    }

    if (!profile.reqSent && !profile.reqReceived) {
      return (
        <FollowActionButton loading={sendFollowRequest.isLoading} onClick={onClickConnect}>
          {'Follow'}
        </FollowActionButton>
      )
    } else if (showSent) {
      return <FollowActionButton disabled>{'Request Sent'}</FollowActionButton>
    } else if (showAccept) {
      return (
        <Box sx={{ display: 'flex', gap: '12px' }}>
          <RequestActionButton kind='reject' onClick={onClickDeclineRequest}>
            Reject
          </RequestActionButton>

          <RequestActionButton kind='accept' onClick={onClickAcceptRequest}>
            Accept
          </RequestActionButton>
        </Box>
      )
    }
    return null
  }

  const onUserAction = (user: ConnectionModel, action: ConnListAction) => {
    if (action == ConnListAction.FOLLOW || action == ConnListAction.ACCEPT_CONN_REQUEST) {
      setSelectedUserForDrawer(user)
    }
  }

  return (
    <UserDetailsContent sx={{ width: '100%', overflow: 'visible', height: 'auto' }}>
      {/* Hero Section with Cover */}
      <Box
        sx={{
          position: 'relative',
          height: '240px',
          background: profile?.profileCoverURL
            ? `url(${profile.profileCoverURL})`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.6) 100%)',
            backdropFilter: 'blur(2px)',
          },
        }}
      >
        {/* Avatar positioned at bottom of hero */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '-60px',
            left: '40px',
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '140px',
              height: '140px',
            }}
          >
            <UserAvatar
              name={`${profile?.firstName} ${profile?.lastName}`}
              url={profile?.profilePicURL}
              borderRadius={100}
              size={140}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: '-5px',
                borderRadius: '50%',
                border: '5px solid #ffffff',
                boxShadow: '0 8px 32px rgba(99, 102, 241, 0.2)',
                zIndex: -1,
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Profile Info Section */}
      <Box
        sx={{
          position: 'relative',
          padding: '72px 40px 32px 40px',
          background: '#ffffff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '24px',
          }}
        >
          {/* User Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '28px',
                fontWeight: 700,
                color: '#0f172a',
                letterSpacing: '-0.02em',
                mb: '8px',
              }}
              className='username'
            >
              {profile.firstName} {profile.lastName}
            </Typography>

            {profile.motto && (
              <Typography
                className='motto'
                sx={{
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#64748b',
                  fontStyle: 'italic',
                  mb: '12px',
                }}
              >
                "{profile.motto}"
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                mt: '12px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  background: 'rgba(99, 102, 241, 0.08)',
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                }}
              >
                <Box
                  sx={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#6366f1',
                  }}
                >
                  Member since {dayjs(profile?.joinDate).format('MMM YYYY')}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Connection Actions */}
          <Box sx={{ mt: '4px' }}>{renderConnectionStatus()}</Box>
        </Box>
      </Box>

      {/* Tabs Section */}
      <Box
        sx={{
          px: '40px',
          pt: '24px',
          pb: '16px',
          width: '100%',
          background: '#ffffff',
          borderTop: '1px solid #e2e8f0',
          zIndex: 5,
          boxShadow: '0 1px 0 0 rgba(0,0,0,0.05)',
        }}
      >
        <ScrollableCapsulesTabBar
          titles={tabTitles}
          selectedIndex={selectedTabIndex}
          onSelectAtIndex={handleTabSelection}
          showScrollButtons={false}
        />
      </Box>

      {/* Content Section */}
      {(topicsList && topics.length == 0 && !connectionsTabSelected) ||
      (connections && connections.length == 0 && connectionsTabSelected) ? (
        <Box
          flexDirection={'column'}
          display={'flex'}
          sx={{
            width: '100%',
            minHeight: '400px',
            background: '#f8fafc',
            padding: '60px 40px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {!connectionsTabSelected && userId && (
              <NoDataPlaceholder
                icon={TopicTypes[selectedTopicType].outlineIcon}
                title={`No ${TopicTypes[selectedTopicType].tabTitle.toLowerCase()} created`}
                subTitle={`${profile?.firstName || ''} has not created any ${TopicTypes[
                  selectedTopicType
                ].title.toLowerCase()} ${
                  selectedTopicType == TopicType.PATTERN || selectedTopicType == TopicType.QUILT
                    ? 'design'
                    : ''
                } yet!`}
              />
            )}
            {connectionsTabSelected && userId && (
              <NoDataPlaceholder
                icon={SVGS.NoConnectionsIcon}
                title={`No Followers`}
                subTitle={`${profile?.firstName || ''} has no any follower yet!`}
              />
            )}
          </Box>
        </Box>
      ) : (
        <Box
          flexDirection={'column'}
          display={'flex'}
          sx={{
            width: '100%',
            background: '#f8fafc',
            padding: '32px 40px 60px 40px',
          }}
        >
          {!connectionsTabSelected && userId && (
            <>
              <TopicItemsGrid
                topics={topics}
                onClickTopicItem={onClickTopic}
                onRemoved={() => {}}
                filter={{ topicType: selectedTopicType, privacyType: PrivacyType.PUBLIC }}
                showAuthor={false}
                showAuthorForPublic={false}
              />
              {canLoadNextPage && (
                <Box m={3} display='flex' justifyContent={'center'}>
                  <LoadingButton
                    variant='text'
                    onClick={loadNextPageData}
                    loading={loadingNextPage}
                    disabled={!loadNextPageData}
                    sx={{
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: loadingNextPage ? '#9ca3af' : colors.meshdomBlue,
                      padding: '12px 24px',
                      borderRadius: '12px',
                      letterSpacing: '-0.01em',

                      '&:hover': {
                        backgroundColor: loadingNextPage
                          ? 'transparent'
                          : 'rgba(59, 130, 246, 0.04)',
                      },

                      '&:disabled': {
                        color: '#9ca3af',
                      },
                    }}
                  >
                    {loadingNextPage ? '' : 'Show More'}
                  </LoadingButton>
                </Box>
              )}
            </>
          )}
          {connectionsTabSelected && userId && (
            <ConnectionsListContext.Provider
              value={{
                items: connections,
                loading: loadingConnections,
                loadingNextPage: loadingConnNextPage,
                canLoadNextPage: canLoadConnNextPage,
                loadNextPageData: loadNextPageConnData,
                total: connections.length, // TODO: API issue here. pagination is not giving totalCount
              }}
            >
              <UserConnections onUserAction={onUserAction} />
            </ConnectionsListContext.Provider>
          )}
        </Box>
      )}
      <CirclesSelectDrawer
        open={selectedUserForDrawer != null}
        user={selectedUserForDrawer}
        onClose={() => setSelectedUserForDrawer(null)}
      />
      <IconButton
        onClick={() => navigate(-1)}
        sx={{
          width: { xs: 36, sm: 44 },
          height: { xs: 36, sm: 44 },
          position: 'absolute',
          left: 16,
          top: 16,
          flexShrink: 0,
          backgroundColor: 'rgba(248, 250, 252, 0.8)',
          border: '1px solid rgba(226, 232, 240, 0.6)',
          borderRadius: '12px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: '#ffffff',
            transform: 'translateX(-2px)',
            borderColor: '#6366f1',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
          },
          '&:active': {
            transform: 'translateX(0)',
          },
        }}
      >
        <SVG src={SVGS.BackArrow} color='#475569' width={18} height={18} />
      </IconButton>
    </UserDetailsContent>
  )
}
